import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check existing session on app start
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    // Sla de ping over voor anonieme bezoekers — voorkomt 401-ruis in de console
    // op marketingpagina's. `wp_has_session` wordt gezet bij login/register en gewist bij logout.
    if (typeof window !== 'undefined' && !window.localStorage.getItem('wp_has_session')) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      // Try to get current user profile using session token cookie
      const response = await axios.get('/user/profile');
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Sessie is verlopen of ongeldig — marker opruimen zodat volgende load stil is
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('wp_has_session');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          pendingToken: response.data.pending_token,
          reason: response.data.reason,
          message: response.data.message,
        };
      }
      const { user: userData } = response.data;

      setUser(userData);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('wp_has_session', '1');
      }
      checkExistingSession();
      toast.success(`Welcome back ${userData.name}!`);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verify2FA = async (pendingToken, code) => {
    try {
      const response = await axios.post('/auth/login/verify-2fa', { pending_token: pendingToken, code });
      const { user: userData } = response.data;
      setUser(userData);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('wp_has_session', '1');
      }
      checkExistingSession();
      toast.success(`Welcome back ${userData.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password, language = 'en', currency = 'EUR') => {
    try {
      const response = await axios.post('/auth/register', { name, email, password, language, currency });
      if (response.data.requires_email_verification) {
        return {
          success: false,
          requiresEmailVerification: true,
          message: response.data.message,
        };
      }
      const { user: userData } = response.data;

      setUser(userData);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('wp_has_session', '1');
      }
      toast.success(`Welcome ${userData.name}! Your free trial has started.`);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      await axios.put('/user/preferences', preferences);
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        ...preferences
      }));
      
      toast.success('Preferences updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update preferences';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('wp_has_session');
      }
      toast.success('Logged out successfully');
      window.location.href = '/';
    }
  };

  const requestMagicLink = async (email) => {
    try {
      const response = await axios.post('/auth/magic-link', { email });
      toast.success(response.data.message || 'Inloglink verstuurd');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Versturen mislukt';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    login,
    verify2FA,
    register,
    logout,
    requestMagicLink,
    checkExistingSession,
    updatePreferences
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};